import { ISignUpLink, ISubscriber } from '@/types';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useNotificationsStore } from './notifications.store';
import { useAuthStore } from './auth.store';
import axios from 'axios';

const rawDataToLink = (data: any, id: number): ISignUpLink => ({
  id,
  name: String(data?.name || ''),
  created: +data?.created || 0,
  lastUsed: +data?.lastUsed || 0,
  createdBy: String(data?.createdBy || ''),
  subscribers: +data?.subscribers || 0,
  visited: +data?.visited || 0,
  groupId: String(data?.group || ''),
});

const apiBase = import.meta.env.VITE_API_URL;

export const useMarketingStore = defineStore('marketingStore', () => {
  const signUpLinks = ref<ISignUpLink[]>([]);
  const signUpLinksMap = computed<Map<number, ISignUpLink>>(() => new Map(signUpLinks.value.map(l => [l.id, l])));
  const notificationsStore = useNotificationsStore();
  const authStore = useAuthStore();

  const linksFetched = ref<boolean>(false);
  const fetchSignUpLinks = (): Promise<void> => {
    return new Promise((resolve, reject) => {

      if (!linksFetched.value) {
        axios<ISignUpLink[]>({
          method: 'GET',
          url: apiBase + `/signUpLinks/`,
          headers: {
            Authorization: 'Bearer ' + authStore.idAuthToken,
          },
        })
          .then((result) => {
            signUpLinks.value = result.data.map((link) => rawDataToLink(link, link.id));
            linksFetched.value = true;
            resolve();
          })
          .catch(err => {
            console.error(err);
            notificationsStore.error('Load SignUp Links Failed');
            reject();
          });
      } else {
        resolve();
      }
    });
  };

  const fetchSignUpLink = (id: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      axios<ISignUpLink>({
        method: 'GET',
        url: apiBase + `/signUpLinks/${id}`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
      })
        .then((resulr) => {
          if (resulr.data) {
            const newLink: ISignUpLink = rawDataToLink(resulr.data, resulr.data.id);
            const newLinks = signUpLinks.value;

            if (signUpLinksMap.value.has(id)) {
              const indexOfLink: number = newLinks.findIndex(l => l.id === id);

              if (indexOfLink > -1) {
                newLinks[indexOfLink] = newLink;
              }
            } else {
              newLinks.push(newLink);
            }

            signUpLinks.value = [...newLinks];

            resolve();
          } else {
            resolve();
          }

        })
        .catch(err => {
          console.error(err);
          reject(err);
        })
    });
  };

  const createSignUpLink = (link: ISignUpLink): Promise<number> => {
    return new Promise((resolve, reject) => {

      axios<number>({
        method: 'POST',
        url: apiBase + `/signUpLinks/`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: link
      })
        .then((result) => {
          fetchSignUpLink(result.data)
            .then(() => {
              resolve(result.data);
            })
            .catch(err => {
              console.error(err);
              reject(err);
            });
        })
        .catch((err) => {
          console.error(err);
          notificationsStore.error('Create SignUp Link Failed');
          reject(err);
        });
    });
  };

  const renameSignUpLink = (id: number, name: string): Promise<void> => {
    return new Promise((resolve, reject) => {

      axios<number>({
        method: 'PUT',
        url: apiBase + `/signUpLinks/${id}/rename`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: {
          name
        }
      })
        .then(() => {
          fetchSignUpLink(id)
            .then(() => {
              resolve();
            })
            .catch(err => {
              console.error(err);
              reject(err);
            });
        })
        .catch((err) => {
          console.error(err);
          notificationsStore.error('Edit SignUp Link Failed');
          reject(err);
        });
    });
  };

  const getCSVContent = (linkId: number): Promise<object[]> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'POST',
        url: apiBase + `/subscribers/get-csv-content`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: { linkId },
      })
        .then((response) => {
          resolve(response.data);
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const updateFileUrlSignUpLink = (id: number, fileUrl: string): Promise<void> => {
    return new Promise((resolve, reject) => {

      axios<number>({
        method: 'PUT',
        url: apiBase + `/signUpLinks/${id}/update-file-url`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: {
          fileUrl
        }
      })
        .then(() => {
          fetchSignUpLink(id)
            .then(() => {
              resolve();
            })
            .catch(err => {
              console.error(err);
              reject(err);
            });
        })
        .catch((err) => {
          console.error(err);
          notificationsStore.error('Edit SignUp Link Failed');
          reject(err);
        });
    });
  };

  const visitSignUpLink = (id: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'PUT',
        url: apiBase + `/signUpLinks/${id}/visit`,
      })
        .then(() => {
          resolve();
        })
        .catch((err) => {
          console.error(err);
          reject(err);
        });
    });
  };

  const getFileUrlLink = (id: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'POST',
        url: apiBase + `/signUpLinks/get-file-url`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
        data: {
          linkId: id
        }
      })
        .then((res) => {
          resolve(
            res.data as string
          );
        })
        .catch((err) => {
          console.error(err);
          reject(err);
        });
    });
  };

  const deleteSignUpLink = (id: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      axios({
        method: 'DELETE',
        url: apiBase + `/signUpLinks/${id}`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
      })
        .then(() => {
          // Remove the link from the local store
          signUpLinks.value = signUpLinks.value.filter(link => link.id !== id);
          notificationsStore.showNotification('Marketing link deleted successfully', 'success');
          resolve();
        })
        .catch((err) => {
          console.error(err);
          notificationsStore.error('Failed to delete marketing link');
          reject(err);
        });
    });
  };

  const fetchLinkSubscribers = (linkId: number): Promise<ISubscriber[]> => {
    return new Promise((resolve, reject) => {
      axios<ISubscriber[]>({
        method: 'GET',
        url: apiBase + `/signUpLinks/${linkId}/subscribers`,
        headers: {
          Authorization: 'Bearer ' + authStore.idAuthToken,
        },
      })
        .then((res) => {
          resolve(Array.isArray(res.data) ? res.data : []);
        })
        .catch((err) => {
          console.error(err);
          notificationsStore.error('Failed to load subscribers');
          reject(err);
        });
    });
  };

  return { signUpLinks, signUpLinksMap, getFileUrlLink, fetchSignUpLinks, fetchSignUpLink, fetchLinkSubscribers, updateFileUrlSignUpLink, renameSignUpLink, createSignUpLink, visitSignUpLink, getCSVContent, deleteSignUpLink };
});